import { COMMA, ENTER, SPACE } from '@angular/cdk/keycodes';
import { Component, Input, OnChanges, OnInit } from '@angular/core';
import {
  AbstractControl,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { MatChipInputEvent } from '@angular/material/chips';

import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { ErrorActions } from 'src/app/error/store/error.slice';

import { ContainerMarketingPropertiesDto } from '@app/api/models';

import { getServerErrorForField } from '../../helpers/form-helper';
import { Language } from '../../interfaces/language';
import { LanguageCodesService } from '../../services/language-codes.service';
import { notBlank } from '../../validators/not-blank.validator';

@Component({
  selector: 'app-localized-marketing-subform',
  templateUrl: './localized-marketing-subform.component.html',
  styleUrls: ['./localized-marketing-subform.component.scss'],
})
export class LocalizedMarketingSubformComponent implements OnChanges, OnInit {
  static DEFAULT_LANGUAGE = 'hun';
  static VALIDATORS = [Validators.required, notBlank];
  separatorKeysCodes: number[] = [ENTER, COMMA, SPACE];

  @Input() formGroupAbstract!: AbstractControl;
  @Input() textareaAutosizeMinRows: number = 1;
  @Input() readOnly = false;

  form!: UntypedFormGroup;
  productTags: { [key: string]: string[] } = {};
  productInputs!: UntypedFormGroup;

  languageSelect: UntypedFormControl = new UntypedFormControl(null);

  constructor(
    private languageCodesService: LanguageCodesService,
    private store: Store,
    private translateService: TranslateService
  ) {}

  static buildForm(title: { [key: string]: ContainerMarketingPropertiesDto }) {
    return new UntypedFormGroup(
      Object.fromEntries(
        Object.entries(title).map(([language, localizedValue]) => [
          language,
          new UntypedFormGroup({
            detailedDescription: new UntypedFormControl(
              localizedValue.detailedDescription
            ),
            videoLink: new UntypedFormControl(localizedValue.videoLink),
            productTags: new UntypedFormControl(localizedValue.productTags),
          }),
        ])
      )
    );
  }

  private static buildSubForm(): AbstractControl<any, any> {
    return new UntypedFormGroup({
      detailedDescription: new UntypedFormControl(null),
      videoLink: new UntypedFormControl(null),
      productTags: new UntypedFormControl([]),
    });
  }

  ngOnInit(): void {
    this.productInputs = new UntypedFormGroup({
      [LocalizedMarketingSubformComponent.DEFAULT_LANGUAGE]:
        new UntypedFormControl(''),
    });

    if (this.form.controls) {
      Object.keys(this.form.controls).forEach((e) => {
        const subform = this.form.controls[e];
        if (!this.productInputs.get(e)) {
          this.productInputs.addControl(e, new UntypedFormControl(''));
        }
        if (subform.value.productTags) {
          this.productTags[e] = subform.value.productTags;
        }
      });
    }
    if (!this.form.get(LocalizedMarketingSubformComponent.DEFAULT_LANGUAGE)) {
      this.form.addControl(
        LocalizedMarketingSubformComponent.DEFAULT_LANGUAGE,
        LocalizedMarketingSubformComponent.buildSubForm()
      );
      this.productInputs.addControl(
        LocalizedMarketingSubformComponent.DEFAULT_LANGUAGE,
        new UntypedFormControl('')
      );
    }
    if (this.readOnly) {
      this.form.disable();
      this.productInputs.disable();
    }
  }

  ngOnChanges(): void {
    if (!this.formGroupAbstract) {
      throw Error('Attribute "formGroupAbstract" is required');
    }
    this.form = this.formGroupAbstract as UntypedFormGroup;
    if (this.form.controls) {
      Object.keys(this.form.controls).forEach((e) => {
        const subform = this.form.controls[e];
        if (subform.value.productTags) {
          this.productTags[e] = subform.value.productTags;
        }
      });
    }
  }

  onAdd(): void {
    const selectedLanguageCode: string | null =
      this.languageSelect?.value?.code;
    if (selectedLanguageCode) {
      if (this.form.contains(selectedLanguageCode)) {
        this.store.dispatch(
          ErrorActions.handleError({
            traceId: 0,
            message: this.translateService.instant(
              'SHARED.LOCALIZED_MARKETING_SUBFORM.ALREADY_ADDED'
            ),
            errorCode: '',
            globalErrors: {},
            fieldErrors: {},
          })
        );
      } else {
        this.form.addControl(
          selectedLanguageCode,
          LocalizedMarketingSubformComponent.buildSubForm()
        );
        this.productInputs.addControl(
          selectedLanguageCode,
          new UntypedFormControl('')
        );

        this.languageSelect.reset();
      }
    } else {
      this.languageSelect.markAsTouched();
      window.setTimeout(() => {
        // keeps control from staying red forever
        this.languageSelect.markAsUntouched();
      }, 3000);
    }
  }

  onDelete(selectedLanguageCode: string): void {
    this.form.removeControl(selectedLanguageCode);
    this.form.markAsDirty();
  }

  getKeys(): string[] {
    if (!this.form) return [];
    if (!this.form.controls) return [];
    return Object.keys(this.form.controls);
  }

  getNameByCode(code: string): string {
    return this.languageCodesService.getNameByCode(code);
  }

  getLanguages(): Language[] {
    return this.languageCodesService.availableLanguages;
  }

  nameFunction(item: Language): string {
    return item.name;
  }

  equalsFunction(a: Language, b: Language): boolean {
    return a && b && a.code === b.code;
  }

  matchFunction(item: Language, searchPattern: string): boolean {
    const fuzzyMatch = (_item: string, _searchPattern: string): boolean => {
      _searchPattern = _searchPattern.toLowerCase();
      _item = _item.toLowerCase();
      _searchPattern = '.*' + _searchPattern.split('').join('.*') + '.*';
      const re = new RegExp(_searchPattern);
      return re.test(_item);
    };

    return (
      fuzzyMatch(item.code, searchPattern) ||
      fuzzyMatch(item.name, searchPattern)
    );
  }

  getServerError(fieldName: string): any {
    return getServerErrorForField(this.form, fieldName);
  }

  addTag(event: MatChipInputEvent, lang: string): void {
    const value = (event.value || '').trim();

    if (value) {
      if (this.productTags[lang]) {
        let tmpProductTags = JSON.parse(JSON.stringify(this.productTags[lang]));
        tmpProductTags.push(value);
        this.productTags = { ...this.productTags, [lang]: tmpProductTags };
      } else {
        this.productTags[lang] = [value];
      }
    }

    event.chipInput.clear();
    this.productInputs.controls[lang].setValue(null);

    this.form.patchValue({ [lang]: { productTags: this.productTags[lang] } });
    this.form.markAsTouched();
  }

  removeTag(tag: string, lang: string): void {
    const index = this.productTags[lang].indexOf(tag);

    if (index >= 0) {
      this.productTags[lang].splice(index, 1);
    }

    this.form.patchValue({ [lang]: { metaTags: this.productTags[lang] } });
  }
}
